import { Box } from "@mui/material";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import type { AnnieChatItem } from "../../hooks/useAnnie";
import { MOTION } from "../../theme/tokens";
import type {
	AnnieClarificationCard as AnnieClarificationCardModel,
	AnnieClarificationOption,
	AnnieFeedbackRequest,
	AnnieNurseChoiceOption,
	AnnieProposalCard as AnnieProposalCardModel,
	AnnieSuggestion,
	AnnieWalkthrough,
} from "../../utils/annieStream";
import { isHeadNurse } from "../../utils/utilFunctions";
import { JumpingDots } from "../UIToolkit/Animations/JumpingDots";
import { AssistantAvatar } from "../UIToolkit/AssistantAvatar/AssistantAvatar";
import { AssistantAvatarMood } from "../UIToolkit/AssistantAvatar/assistantAvatarConfig";
import { ChatAvatarRow } from "../UIToolkit/Chat/ChatAvatarRow";
import { ChatMessageBubble } from "../UIToolkit/Chat/ChatMessageBubble";
import { ChatMessageList } from "../UIToolkit/Chat/ChatMessageList";
import { AnnieAnswerBubble } from "./AnnieAnswerBubble";
import { AnnieClarificationCard } from "./AnnieClarificationCard";
import { AnnieEmptyStateCapabilities } from "./AnnieEmptyStateCapabilities";
import { AnnieProposalCard } from "./AnnieProposalCard";
import { AnnieRecommendationsFeed } from "./AnnieRecommendationsFeed";
import type { AnnieStudioNudge } from "./AnnieStudio/useAnnieStudioNudge";
import { AnnieThinkingLine } from "./AnnieThinkingLine";
import { ANNIE_TURN_AVATAR_SIZE, type AnnieConversationAvatar, layoutAnnieTurns } from "./annieTurns";

type Props = {
	items: AnnieChatItem[];
	/** Transient progress line ("Finding Amir…") while the stream is running. */
	statusText: string | null;
	/** The in-flight turn's status lines so far; emptied once the answer starts. */
	workSteps: readonly string[];
	activeWorkStep: string | null;
	isStreaming: boolean;
	/** A past session's transcript is being fetched. */
	isLoadingSession: boolean;
	onChoose: (itemId: string, card: AnnieClarificationCardModel, option: AnnieClarificationOption) => void;
	onSubmitTimes: (itemId: string, card: AnnieClarificationCardModel, values: { start_time: string; end_time: string }) => void;
	onApprove: (itemId: string, card: AnnieProposalCardModel) => void;
	onReject: (itemId: string, card: AnnieProposalCardModel) => void;
	onSaveFeedback: (itemId: string, conversationId: number, replacement: AnnieFeedbackRequest) => Promise<boolean>;
	onPromptSelect: (prompt: string) => void;
	/** Whole-row tap on an approval recommendation: fills the composer AND auto-sends (not prefill-only). */
	onOpeningPromptSend: (prompt: string) => void;
	/** Tap on a follow-up chip: asks the assistant about the related topic. */
	onSuggestionSelect: (suggestion: AnnieSuggestion) => void;
	onNurseChoiceSelect: (itemId: string, name: string, option: AnnieNurseChoiceOption) => void;
	/** Absent when the panel has no walkthrough host mounted — the offer chip is then never rendered. */
	onStartWalkthrough?: (walkthrough: AnnieWalkthrough) => void;
	/** Regenerate a persisted answer: re-runs its question and replaces the answer in place. */
	onRegenerate: (answerItemId: string, conversationId: number) => void;
	/** Panel-open state — gates the recommendations fetch so it never runs in the background. */
	isOpen: boolean;
	promptSelectionDisabled: boolean;
	/** The user's Annie and her live mood; null renders the standard (avatar-free) conversation. */
	avatar: AnnieConversationAvatar | null;
	/** The user's name for Annie. */
	assistantName: string;
	studioNudge: AnnieStudioNudge;
	onCustomizeFromNudge: () => void;
};

const OPENING_EXIT_DISTANCE_PX = 24;
const OPENING_EXIT_DURATION_S = MOTION.DURATION.OPENING_EXIT / 1000;
const EASE_OUT = [0.23, 1, 0.32, 1] as const;

/** The Annie conversation column: bubbles, cards, thinking indicator, empty state. */
export const AnnieMessages: FC<Props> = ({
	items,
	statusText,
	workSteps,
	activeWorkStep,
	isStreaming,
	isLoadingSession,
	onChoose,
	onSubmitTimes,
	onApprove,
	onReject,
	onSaveFeedback,
	onPromptSelect,
	onOpeningPromptSend,
	onSuggestionSelect,
	onNurseChoiceSelect,
	onStartWalkthrough,
	onRegenerate,
	isOpen,
	promptSelectionDisabled,
	avatar,
	assistantName,
	studioNudge,
	onCustomizeFromNudge,
}) => {
	const { t } = useTranslation();
	const reduceMotion = useReducedMotion();
	const { chosenDepartment } = useChosenDepartment();
	const { userInfo } = useAuth();

	if (isLoadingSession) {
		return (
			<Box data-testid="annie-session-loading" sx={{ display: "flex", justifyContent: "center", py: 3 }}>
				<JumpingDots />
			</Box>
		);
	}

	const isOpening = items.length === 0 && !isStreaming;

	// id/kind cover choose()'s in-place card swap (length unchanged); the rest cover appends,
	// streamed-token growth, status, and the streaming toggle.
	const lastItem = items.at(-1);
	const lastTextLength = lastItem && "text" in lastItem ? lastItem.text.length : 0;
	const scrollKey = `${items.length}:${lastItem?.id ?? ""}:${lastItem?.kind ?? ""}:${lastTextLength}:${workSteps.length}:${statusText ?? ""}:${isStreaming}`;

	// The thinking indicator resolves into the answer: once a read turn is streaming tokens into its
	// answer bubble (or a write turn has surfaced its card) and there's no pending status line, visible
	// output has arrived — so drop the dots instead of dangling them beneath the answer. It still shows
	// while waiting (before the first token) and whenever a fresh status line is set (next tool round).
	const lastIsAssistantOutput = lastItem?.kind === "assistant" || lastItem?.kind === "proposal" || lastItem?.kind === "clarification";
	const showThinking = isStreaming && (statusText !== null || !lastIsAssistantOutput);

	const turns = layoutAnnieTurns(items);
	const turnAvatar = (item: AnnieChatItem, index: number): ReactNode | null => {
		if (avatar === null || !turns.starts.has(index)) return null;
		const isNewest = index === turns.newestStart;
		return (
			<AssistantAvatar
				config={avatar.config}
				mood={isNewest ? avatar.mood : AssistantAvatarMood.Idle}
				size={ANNIE_TURN_AVATAR_SIZE}
				animated={isNewest}
				dataTestId={`annie-turn-avatar-${item.id}`}
			/>
		);
	};
	const withTurnColumn = (item: AnnieChatItem, index: number, node: ReactNode): ReactNode => {
		if (avatar === null || item.kind === "user") return node;
		return (
			<ChatAvatarRow key={item.id} avatar={turnAvatar(item, index)} columnWidth={ANNIE_TURN_AVATAR_SIZE}>
				{node}
			</ChatAvatarRow>
		);
	};

	const latestUser = [...items].reverse().find((item) => item.kind === "user");
	const latestUserIndex = items.findIndex((item) => item.id === latestUser?.id);
	const renderItem = (item: AnnieChatItem, index: number): ReactNode => {
		switch (item.kind) {
			case "user":
				return (
					<ChatMessageBubble key={item.id} sender="user" dataTestId={`annie-message-${item.id}`}>
						{item.text}
					</ChatMessageBubble>
				);
			case "assistant":
				return (
					<AnnieAnswerBubble
						key={item.id}
						item={item}
						assistantName={assistantName}
						isLatest={item.id === items.at(-1)?.id}
						onRegenerate={onRegenerate}
						onSuggestionSelect={onSuggestionSelect}
						onNurseChoiceSelect={onNurseChoiceSelect}
						nurseChoicesDisabled={isStreaming || promptSelectionDisabled || index < latestUserIndex}
						onStartWalkthrough={onStartWalkthrough}
						onSaveFeedback={onSaveFeedback}
					/>
				);
			case "error":
				return (
					<ChatMessageBubble key={item.id} sender="assistant" tone="error" dataTestId={`annie-message-${item.id}`}>
						{item.text}
					</ChatMessageBubble>
				);
			case "proposal":
				return <AnnieProposalCard key={item.id} item={item} onApprove={() => onApprove(item.id, item.card)} onReject={() => onReject(item.id, item.card)} />;
			case "clarification":
				return (
					<AnnieClarificationCard
						key={item.id}
						item={item}
						onChoose={(option) => onChoose(item.id, item.card, option)}
						onSubmitTimes={(values) => onSubmitTimes(item.id, item.card, values)}
					/>
				);
			default:
				// The switch is exhaustive over AnnieChatItem["kind"].
				return null;
		}
	};

	return (
		<AnimatePresence initial={false} mode="wait">
			{isOpening ? (
				<motion.div
					key="annie-opening"
					data-testid="annie-opening-transition"
					initial={false}
					exit={reduceMotion ? { opacity: 0 } : { opacity: 0, transform: `translateY(-${OPENING_EXIT_DISTANCE_PX}px)` }}
					transition={{ duration: OPENING_EXIT_DURATION_S, ease: EASE_OUT }}
					style={{ width: "100%" }}
				>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
						{isHeadNurse(userInfo.role) && (
							<AnnieRecommendationsFeed
								departmentId={chosenDepartment.department_id}
								isOpen={isOpen}
								disabled={promptSelectionDisabled}
								onApprovalSelect={onOpeningPromptSend}
								assistantName={assistantName}
							/>
						)}
						<AnnieEmptyStateCapabilities
							disabled={promptSelectionDisabled}
							onPromptSelect={onPromptSelect}
							avatarConfig={avatar === null ? null : avatar.config}
							studioNudge={studioNudge}
							onCustomizeFromNudge={onCustomizeFromNudge}
						/>
					</Box>
				</motion.div>
			) : (
				<motion.div key="annie-conversation" data-testid="annie-conversation-content" style={{ width: "100%" }}>
					<ChatMessageList dataTestId="annie-messages" scrollKey={scrollKey}>
						{items.map((item, index) => withTurnColumn(item, index, renderItem(item, index)))}

						{showThinking && (
							<AnnieThinkingLine
								avatar={avatar}
								assistantName={assistantName}
								turnStarted={turns.newestStart !== null}
								text={statusText ?? t("annie.thinking")}
								steps={workSteps}
								activeStep={activeWorkStep}
							/>
						)}
					</ChatMessageList>
				</motion.div>
			)}
		</AnimatePresence>
	);
};
